import { useEffect, useState, type MouseEvent } from "react";
import { Upload } from "antd";
import type { UploadChangeParam, UploadFile } from "antd/es/upload/interface";
import { DeleteOutlined } from "@ant-design/icons";

import { ReactComponent as UploadSVG } from "@/assets/icons/image.svg";
import { ALLOWED_IMAGE_MIME_TYPES, AssetsUtils } from "@/shared/services/assets/assets.utils";

import uploadPhotoStyle from "@/ui-components/commonComponents/UploadPhoto/style.module.css";

const { Dragger } = Upload;

export type LandingImageFormDraggerProps = {
  id: string;
  file?: UploadFile;
  onChange?: (file: UploadFile | undefined) => void;
};

export function LandingImageFormDragger({ id, file, onChange }: LandingImageFormDraggerProps) {
  const [previewUrl, setPreviewUrl] = useState("");

  // For freshly uploaded files (originFileObj), allocate a blob URL via createObjectURL
  // — the cleanup paired with URL.revokeObjectURL prevents leaks. For files with only a
  // remote URL (e.g. the version flow pre-populating with the source template's image),
  // use that URL directly so the dragger renders the same image preview.
  useEffect(() => {
    const fileObj = file?.originFileObj;
    if (fileObj) {
      const url = URL.createObjectURL(fileObj);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(file?.url ?? "");
  }, [file?.uid, file?.originFileObj, file?.url]);

  const handleDraggerChange = (info: UploadChangeParam<UploadFile>) => {
    onChange?.(info.fileList[0]);
  };

  const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onChange?.(undefined);
  };

  return (
    <div className={uploadPhotoStyle.uploaderContainer}>
      <Dragger
        id={id}
        accept={ALLOWED_IMAGE_MIME_TYPES.join(",")}
        maxCount={1}
        showUploadList={false}
        listType="picture-card"
        className={uploadPhotoStyle.photoUploader}
        fileList={file ? [file] : []}
        onChange={handleDraggerChange}
        beforeUpload={(file) => {
          if (!AssetsUtils.imageValidation(file)) {
            return Upload.LIST_IGNORE;
          }
          return false;
        }}
      >
        {previewUrl ? (
          <div className={uploadPhotoStyle.imgContainer}>
            <img src={previewUrl} alt="Landing page preview" className={uploadPhotoStyle.previewImage} />
            <button
              type="button"
              className={uploadPhotoStyle.deleteButton}
              onClick={handleRemove}
              aria-label="Remove landing photo"
            >
              <DeleteOutlined className={uploadPhotoStyle.deleteIcon} />
            </button>
          </div>
        ) : (
          <div>
            <UploadSVG aria-hidden={true} />
            <p className={uploadPhotoStyle.uploadTitle}>Add Landing Photo</p>
          </div>
        )}
      </Dragger>
    </div>
  );
}
